/** Object being injected in global (window) scope by consumer app */
interface UpflowyPlugins {
  /** Called by plugin to register itself */
  register(config: UpflowyPluginsRegistrationConfig): unknown;
}

interface UpflowyPluginsRegistrationConfig {
  /** Identifier for plugin */
  pluginId?: string;
  /** Callback when customer app has finished loading. Called once per flow */
  onFlowReady?: (flowId: string) => void;
  /** Called after new step is loaded */
  onStepReady?: (stepId: string | number, base: HTMLElement) => void;
  /** Called before next step is loaded */
  onStepExit?: (stepId: string | number) => void;
}

const plugins: Record<string, UpflowyPluginsRegistrationConfig> = {};

type StepReadyEvent = {
  type: 'StepReady';
  stepId: string | number;
  base: HTMLElement;
};

type FlowReadyEvent = {
  type: 'FlowReady';
  flowId: string;
};

const activeEvents: {
  stepReady: {
    event?: StepReadyEvent;
    notified: Set<number | string>;
  };
  flowReady: {
    event?: FlowReadyEvent;
    notified: Set<number | string>;
  };
} = {
  stepReady: { notified: new Set() },
  flowReady: { notified: new Set() },
};

const upflowy: UpflowyPlugins = {
  register(config: UpflowyPluginsRegistrationConfig): () => void {
    const id = config.pluginId;
    // Only use one plugin instance
    if (!id || plugins[id])
      return () => {
        /* empty */
      };
    plugins[id] = config;
    // Make sure we notify late comers
    if (activeEvents.flowReady.event) {
      config.onFlowReady?.(activeEvents.flowReady.event.flowId);
      activeEvents.flowReady.notified.add(id);
    }
    if (activeEvents.stepReady.event) {
      config.onStepReady?.(activeEvents.stepReady.event.stepId, activeEvents.stepReady.event.base);
      activeEvents.stepReady.notified.add(id);
    }
    return () => {
      delete plugins[id];
    };
  },
};

declare global {
  interface Window {
    UpflowyPlugins: UpflowyPlugins;
  }
}

window.UpflowyPlugins = upflowy;

export const onFlowReady = (flowId: string) => {
  activeEvents.flowReady = {
    event: {
      type: 'FlowReady',
      flowId,
    },
    notified: new Set(Object.keys(plugins)),
  };
  Object.values(plugins).forEach((plugin) => {
    plugin.onFlowReady?.(flowId);
  });
};

export const onStepReady = (stepId: string | number, base: HTMLElement) => {
  activeEvents.stepReady = {
    event: {
      type: 'StepReady',
      stepId,
      base,
    },
    notified: new Set(Object.keys(plugins)),
  };
  Object.values(plugins).forEach((plugin) => {
    plugin.onStepReady?.(stepId, base);
  });
};

export const onStepExit = (stepId: string | number) => {
  Object.values(plugins).forEach((plugin) => {
    plugin.onStepExit?.(stepId);
  });
};
